
import React, { useContext, useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Grid
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import { CustomCard } from "../../common/Customcard";
import DrawerComponent from "../../common/DrawerComponent";
import "../Finance.css"
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Tooltip } from "@mui/material";
import toast, { Toaster } from 'react-hot-toast';
import { UserContext } from "../../../UserContext";
import StyledTable from "../../StyledTable";


const ProductService = () => {
    const [data, setData] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const { role, user_id } = useContext(UserContext);

    const [formData, setFormData] = useState({
        category: "",
        name: "",
        description: "",
        tech_stack: "",
        industry: "",
        gst_percentage: "",
        created_by: user_id
    });

    const fetchData = async () => {
        try {
            const response = await axios.get("/api/product-detail/");
            setData(response.data);
        } catch (error) {
            console.error("Fetch error:", error);
        }
    };

    const handleDeleteClick = (row) => {
        setItemToDelete(row);
        setOpenConfirmDialog(true);
    };


    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/product-detail/?id=${itemToDelete.id}`);
            toast.success("Deleted successfully!");
            fetchData();
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("Delete failed!");
        } finally {
            setOpenConfirmDialog(false);
            setItemToDelete(null);
        }
    };



    useEffect(() => {
        fetchData();
    }, []);

    const handleOpen = () => {
        setIsEdit(false);
        setFormData({
            category: "",
            name: "",
            description: "",
            tech_stack: "",
            industry: "",
            gst_percentage: ""
        });
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditingId(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            if (isEdit) {
                await axios.put(`/api/product-detail/?id=${editingId}`, formData);
            } else {
                await axios.post("/api/product-detail/", formData);
            }
            toast.success(`${isEdit ? "Updated" : "Created"}Product/Services Successfully!`);
            fetchData();
            handleClose();
        } catch (error) {
            const backendMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                "Submission failed. Please try again.";
            toast.error(backendMessage);
        }
    };

    const handleEdit = (row) => {
        setFormData(row);
        setEditingId(row.id);
        setIsEdit(true);
        setOpenDialog(true);
    };


    const columns = [
        { field: "sno", headerName: "S.No", width: "60px" },
        { field: "category", headerName: "Category", width: "120px" },
        {
            field: "name", headerName: "Product / Service Name", flex: 1,
            renderCell: (params) => {
                const { name } = params.row;
                return (
                    <Box display="flex" flexDirection="column" width="100%" className="hide-scrollbar" sx={{ maxHeight: 80, overflowY: "auto", overflowX: "hidden", p: 1 }}>
                        <Typography variant="small" sx={{ fontWeight: 500, color: "#333", whiteSpace: "normal", wordBreak: "break-word", textOverflow: "ellipsis", p: 1 }}>
                            {name}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: "description",
            headerName: "Description",
            flex: 1,
            renderCell: (params) => {
                const { description } = params.row;
                return (
                    <Box display="flex" flexDirection="column" width="100%" className="hide-scrollbar" sx={{ maxHeight: 80, overflowY: "auto", overflowX: "hidden", p: 1 }}>
                        <Typography variant="small" sx={{ fontWeight: 500, color: "#333", whiteSpace: "normal", wordBreak: "break-word", textOverflow: "ellipsis", p: 1 }}>
                            {description}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: "tech_stack", headerName: "Technology Stack",
            renderCell: (params) => {
                const { tech_stack } = params.row;
                return (
                    <Box display="flex" flexDirection="column" width="100%" className="hide-scrollbar" sx={{ maxHeight: 80, overflowY: "auto", overflowX: "hidden", p: 1 }}>
                        <Typography variant="small" sx={{ fontWeight: 500, color: "#333", whiteSpace: "normal", wordBreak: "break-word", textOverflow: "ellipsis", p: 1 }}>
                            {tech_stack}
                        </Typography>
                    </Box>
                );
            },
        },
        { field: "industry", headerName: "Target Industry", width: "160px" },
        { field: "gst_percentage", headerName: "GST %" },
        {
            field: "actions",
            headerName: "Actions",
            width: "100px",
            renderCell: ({ row }) => (
                <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }

    ];

    return (
        <Box sx={{
            position: "relative"
        }}>
            <div className="header-actions">
                <Button variant="contained" color="primary" onClick={handleOpen}>
                    + Add Item
                </Button>
            </div>

            <StyledTable columns={columns} data={data} itemsPerPage={4} />

            {/* Dialog Form */}
            <DrawerComponent open={openDialog} onClose={handleClose} title={isEdit ? "Edit Item" : "Add Product / Service"}>
                <Grid container spacing={2}>
                    {/* Category */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Category"
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            variant="outlined"
                        />
                    </Grid>


                    {/* Product / Service Name */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Product / Service Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            variant="outlined"
                        />
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                        <TextField
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            fullWidth
                            variant="outlined"
                            multiline
                            rows={2}
                        />
                    </Grid>

                    {/* Technology Stack */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Technology Stack"
                            name="tech_stack"
                            value={formData.tech_stack}
                            onChange={handleChange}
                            variant="outlined"
                        />
                    </Grid>

                    {/* Target Industry */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Target Industry"
                            name="industry"
                            value={formData.industry}
                            onChange={handleChange}
                            variant="outlined"
                        />
                    </Grid>

                    {/* GST Percentage */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="GST Percentage"
                            name="gst_percentage"
                            value={formData.gst_percentage}
                            onChange={handleChange}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12} display="flex" justifyContent="center" mt={4}>
                        <Button
                            type="button"
                            variant="contained"
                            onClick={handleSubmit}
                            sx={{
        backgroundColor: '#fb7102', // Your custom orange color
        color: 'white',             // Ensures text is readable
        '&:hover': {
            backgroundColor: '#e59002', // A slightly darker shade for hover
        },
    }}
                        // disabled={isSubmitButtonDisabled}
                        >
                            Submit
                        </Button>
                    </Grid>

                </Grid>
            </DrawerComponent>
            <Dialog
                open={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete{" "}
                        <strong>{itemToDelete?.name}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmDialog(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Toaster position="top-right" />

        </Box>
    );
};

export default ProductService;
